"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.cowBuildTrade = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const types_1 = require("@shapeshiftoss/types");
const api_1 = require("../../../api");
const erc20Allowance_abi_1 = require("../../utils/abi/erc20Allowance-abi");
const bignumber_1 = require("../../utils/bignumber");
const helpers_1 = require("../../utils/helpers/helpers");
const constants_1 = require("../utils/constants");
const cowService_1 = require("../utils/cowService");
const helpers_2 = require("../utils/helpers/helpers");
async function cowBuildTrade(deps, input) {
    try {
        const { sellAsset, buyAsset, sellAmountBeforeFeesCryptoBaseUnit: sellAmountExcludeFeeCryptoBaseUnit, bip44Params, wallet, } = input;
        const { adapter, web3 } = deps;
        const { assetReference: sellAssetErc20Address, assetNamespace: sellAssetNamespace } = (0, caip_1.fromAssetId)(sellAsset.assetId);
        const { assetReference: buyAssetErc20Address, chainId: buyAssetChainId } = (0, caip_1.fromAssetId)(buyAsset.assetId);
        if (sellAssetNamespace !== 'erc20') {
            throw new api_1.SwapError('[cowBuildTrade] - Sell asset needs to be ERC-20 to use CowSwap', {
                code: api_1.SwapErrorTypes.UNSUPPORTED_PAIR,
                details: { sellAssetNamespace },
            });
        }
        if (buyAssetChainId !== types_1.KnownChainIds.EthereumMainnet) {
            throw new api_1.SwapError('[cowBuildTrade] - Buy asset needs to be on ETH mainnet to use CowSwap', {
                code: api_1.SwapErrorTypes.UNSUPPORTED_PAIR,
                details: { buyAssetChainId },
            });
        }
        const buyToken = buyAsset.assetId !== caip_1.ethAssetId ? buyAssetErc20Address : constants_1.COW_SWAP_ETH_MARKER_ADDRESS;
        const receiveAddress = await adapter.getAddress({ wallet, bip44Params });
        /**
         * /v1/quote
         * params: {
         * sellToken: contract address of token to sell
         * buyToken: contractAddress of token to buy
         * receiver: receiver address can be defaulted to "0x0000000000000000000000000000000000000000"
         * validTo: time duration during which quote is valid (eg : 1654851610 as timestamp)
         * appData: appData for the CowSwap quote that can be used later, can be defaulted to "0x0000000000000000000000000000000000000000000000000000000000000000"
         * partiallyFillable: false
         * from: sender address can be defaulted to "0x0000000000000000000000000000000000000000"
         * kind: "sell" or "buy"
         * sellAmountBeforeFee / buyAmountAfterFee: amount in base unit
         * }
         */
        const quoteResponse = await cowService_1.cowService.post(`${deps.apiUrl}/v1/quote/`, {
            sellToken: sellAssetErc20Address,
            buyToken,
            receiver: receiveAddress,
            validTo: (0, helpers_2.getNowPlusThirtyMinutesTimestamp)(),
            appData: constants_1.DEFAULT_APP_DATA,
            partiallyFillable: false,
            from: receiveAddress,
            kind: constants_1.ORDER_KIND_SELL,
            sellAmountBeforeFee: sellAmountExcludeFeeCryptoBaseUnit,
        });
        const { data: { quote: { buyAmount: buyAmountCryptoBaseUnit, sellAmount: quoteSellAmountExcludeFeeCryptoBaseUnit, feeAmount: feeAmountInSellTokenCryptoBaseUnit, }, }, } = quoteResponse;
        const sellAssetUsdRate = await (0, helpers_2.getUsdRate)(deps, sellAsset);
        const sellAssetTradeFeeUsd = (0, bignumber_1.bnOrZero)(feeAmountInSellTokenCryptoBaseUnit)
            .div((0, bignumber_1.bn)(10).exponentiatedBy(sellAsset.precision))
            .multipliedBy((0, bignumber_1.bnOrZero)(sellAssetUsdRate))
            .toString();
        const buyAmountCryptoPrecision = (0, bignumber_1.bn)(buyAmountCryptoBaseUnit).div((0, bignumber_1.bn)(10).exponentiatedBy(buyAsset.precision));
        const quoteSellAmountCryptoPrecision = (0, bignumber_1.bn)(quoteSellAmountExcludeFeeCryptoBaseUnit).div((0, bignumber_1.bn)(10).exponentiatedBy(sellAsset.precision));
        const rate = buyAmountCryptoPrecision.div(quoteSellAmountCryptoPrecision).toString();
        const data = (0, helpers_1.getApproveContractData)({
            web3,
            spenderAddress: constants_1.COW_SWAP_VAULT_RELAYER_ADDRESS,
            contractAddress: sellAssetErc20Address,
        });
        const feeDataOptions = await adapter.getFeeData({
            to: sellAssetErc20Address,
            value: '0',
            chainSpecific: { from: receiveAddress, contractData: data },
        });
        const feeData = feeDataOptions['fast'];
        const trade = {
            rate,
            feeData: {
                networkFeeCryptoBaseUnit: '0',
                chainSpecific: {
                    estimatedGas: feeData.chainSpecific.gasLimit,
                    gasPriceCryptoBaseUnit: feeData.chainSpecific.gasPrice,
                },
                buyAssetTradeFeeUsd: '0',
                sellAssetTradeFeeUsd,
            },
            sellAmountBeforeFeesCryptoBaseUnit: sellAmountExcludeFeeCryptoBaseUnit,
            buyAmountCryptoBaseUnit,
            sources: constants_1.DEFAULT_SOURCE,
            buyAsset,
            sellAsset,
            bip44Params,
            receiveAddress,
            feeAmountInSellTokenCryptoBaseUnit,
            sellAmountDeductFeeCryptoBaseUnit: quoteSellAmountExcludeFeeCryptoBaseUnit,
        };
        const approvalRequired = await (0, helpers_1.isApprovalRequired)({
            adapter,
            sellAsset,
            allowanceContract: constants_1.COW_SWAP_VAULT_RELAYER_ADDRESS,
            receiveAddress,
            sellAmountExcludeFeeCryptoBaseUnit,
            web3: deps.web3,
            erc20AllowanceAbi: erc20Allowance_abi_1.erc20AllowanceAbi,
        });
        if (approvalRequired) {
            trade.feeData.chainSpecific.approvalFeeCryptoBaseUnit = (0, bignumber_1.bnOrZero)(feeData.chainSpecific.gasLimit)
                .multipliedBy((0, bignumber_1.bnOrZero)(feeData.chainSpecific.gasPrice))
                .toString();
        }
        return trade;
    }
    catch (e) {
        if (e instanceof api_1.SwapError)
            throw e;
        throw new api_1.SwapError('[cowBuildTrade]', {
            cause: e,
            code: api_1.SwapErrorTypes.TRADE_QUOTE_FAILED,
        });
    }
}
exports.cowBuildTrade = cowBuildTrade;
